const SESSION_SLOT_DURATION_MS = 50 * 60 * 1000;

/** Tempo que a vaga fica reservada no sistema antes do pagamento (alinhado ao back). */
export const SCHEDULING_RESERVATION_HOLD_MINUTES = 30;

export const SCHEDULING_RESERVATION_COPY = {
	holdNotice: `Este horário está reservado para você por ${SCHEDULING_RESERVATION_HOLD_MINUTES} minutos. Conclua o pagamento para confirmar a consulta — caso contrário, a vaga volta a ficar disponível.`,
	stepActionHint: `Ao continuar, reservamos este horário por ${SCHEDULING_RESERVATION_HOLD_MINUTES} minutos enquanto você paga.`,
	pendingSlotBadge: 'Sua reserva',
} as const;

export function isSlotHeldByPendingSession(
	slot: AvailableDates.SessionTimeSlot,
	sessions: Sessions.Session[],
): boolean {
	if (!sessions.length) return false;
	const slotMs = new Date(slot.start_time).getTime();
	return sessions.some((s) => new Date(s.selected_date).getTime() === slotMs);
}

export function isPayablePendingSession(
	session: Sessions.Session | undefined,
	professionalId: string,
): session is Sessions.Session {
	if (!session || !professionalId) return false;
	if (session.status !== 'CREATED') return false;
	const proId = session.professional_id ?? session.professional?.id;
	return proId === professionalId;
}

export function sessionMatchesSelection(
	session: Sessions.Session,
	selection: {
		selected_date: string;
		available_date_id: string;
		professional_id: string;
	},
): boolean {
	const sessionTime = new Date(session.selected_date).getTime();
	const selectionTime = new Date(selection.selected_date).getTime();
	const proId = session.professional_id ?? session.professional?.id;
	return (
		proId === selection.professional_id &&
		session.available_date_id === selection.available_date_id &&
		sessionTime === selectionTime
	);
}

export function buildSlotFromSession(
	session: Sessions.Session,
): AvailableDates.SessionTimeSlot {
	const startDate = new Date(session.selected_date);
	const endDate = new Date(startDate.getTime() + SESSION_SLOT_DURATION_MS);
	return {
		start_time: session.selected_date,
		end_time: endDate.toISOString(),
		available_date_id: session.available_date_id ?? '',
	};
}

/** Chave local yyyy-MM-dd para comparar com o date picker. */
export function isoDateKeyFromInstant(isoDate: string): string {
	const d = new Date(isoDate);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
		2,
		'0',
	)}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Rótulo dd/MM/yyyy usado pelo AvailableDatePicker. */
export function formatDatePickerLabelFromInstant(isoDate: string): string {
	const d = new Date(isoDate);
	const day = String(d.getDate()).padStart(2, '0');
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const year = d.getFullYear();
	return `${day}/${month}/${year}`;
}

export function getPayablePendingSessions(
	sessions: Sessions.Session[] | undefined,
	professionalId: string,
): Sessions.Session[] {
	if (!sessions?.length || !professionalId) return [];
	return sessions.filter((s) => isPayablePendingSession(s, professionalId));
}

export function findPendingSessionMatchingSelection(
	sessions: Sessions.Session[] | undefined,
	selection: {
		selected_date: string;
		available_date_id: string;
		professional_id: string;
	},
): Sessions.Session | undefined {
	if (!sessions?.length) return undefined;
	return sessions.find((s) => sessionMatchesSelection(s, selection));
}

export function mergeAllPendingSlotsIntoList(
	slots: AvailableDates.SessionTimeSlot[],
	pendingSessions: Sessions.Session[],
	selectedDayKey: string,
): AvailableDates.SessionTimeSlot[] {
	return pendingSessions.reduce(
		(acc, session) =>
			mergePendingSlotIntoList(acc, session, selectedDayKey),
		slots,
	);
}

export function mergePendingSlotIntoList(
	slots: AvailableDates.SessionTimeSlot[],
	pending: Sessions.Session | undefined,
	selectedDayKey: string,
): AvailableDates.SessionTimeSlot[] {
	if (!pending || pending.status !== 'CREATED') {
		return slots;
	}
	const proId = pending.professional_id ?? pending.professional?.id ?? '';
	if (!proId) {
		return slots;
	}
	if (isoDateKeyFromInstant(pending.selected_date) !== selectedDayKey) {
		return slots;
	}

	const pendingSlot = buildSlotFromSession(pending);
	const pendingStart = new Date(pendingSlot.start_time).getTime();
	const merged = slots.some(
		(s) => new Date(s.start_time).getTime() === pendingStart,
	)
		? slots
		: [...slots, pendingSlot];

	return merged.sort(
		(a, b) =>
			new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
	);
}

/**
 * Histórico para upsell de clube: true só quando a API confirma outra consulta além da atual.
 * undefined = ainda carregando (não decide esconder).
 */
/** Sessão sintética para revisar pagamento antes de `POST /sessions`. */
export function buildSchedulingPreviewSession(
	payload: Sessions.Create,
	professional?: Professionals.Professional | null,
): Sessions.Session {
	const now = new Date();
	return {
		id: '',
		available_date_id: payload.available_date_id ?? '',
		service_id: payload.service_id ?? '',
		professional_id: payload.professional_id ?? '',
		parent_session: null,
		type: payload.type ?? 'DEFAULT',
		status: 'CREATED',
		status_description: null,
		payments: [],
		is_online: payload.is_online ?? false,
		selected_date: payload.selected_date,
		meets_url: null,
		googleCalendarEventId: null,
		scheduling_error: null,
		professional_notes: null,
		professional_private_notes: null,
		user_notes: null,
		user_private_notes: null,
		payment_done_at: null,
		started_at: null,
		finished_at: null,
		created_at: now,
		updated_at: now,
		user_id: '',
		professional: (professional ?? {}) as Professionals.Professional,
		user: {} as Users.User,
	};
}

export const SCHEDULING_PREVIEW_SESSION_ID = '__scheduling_preview__';

export function resolveSchedulingHistoryForClubUpsell(
	response: Sessions.GetSessionsRespose | undefined,
	sessionsLoaded: boolean,
	currentSessionId: string,
): boolean | undefined {
	if (!sessionsLoaded || response === undefined) return undefined;
	const total = response.total ?? 0;
	if (total > 1) return true;
	if (total === 0) return false;
	const first = response.data?.[0];
	return first != null && first.id !== currentSessionId;
}
